import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonInputPasswordToggle,
    IonItem,
    IonLabel,
    IonList,
    IonNote,
    IonPage,
    IonSpinner,
    IonThumbnail,
    IonTitle,
    IonToolbar,
    isPlatform
} from '@ionic/react';
import { IonButtons } from '@ionic/react';
import { Browser } from '@capacitor/browser';
import { Haptics, NotificationType } from '@capacitor/haptics';
import { addOutline, arrowBack, bluetooth, checkmark, close, lockClosed, openOutline, qrCode, refreshOutline, settingsOutline, shieldCheckmarkOutline, wifi } from 'ionicons/icons';
import { useEffect, useMemo, useState } from 'react';
import InfoCard from '../components/InfoCard';
import SetupStepProgress from '../components/SetupStepProgress';
import {
    BarcodeScanner,
    BarcodeFormat,
    BarcodesScannedEvent,
} from '@capacitor-mlkit/barcode-scanning';

import styles from './AddDeviceModal.module.css';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import OCGatewayClient from '../client/OCGatewayClient';
import { useTranslation } from 'react-i18next';
import { BleProvisioningTransport } from '../provisioning/bleProvisioningTransport';
import { ProvisioningCandidate, ProvisioningTransport, WifiNetwork } from '../provisioning/deviceProvisioning';
import { SoftApProvisioningTransport } from '../provisioning/softApProvisioningTransport';
import { useBleProvisioningScan } from '../provisioning/useBleProvisioningScan';
import { normalizeProvisioningErrorMessage, provisioningErrorTranslationKeys } from '../provisioning/wifiProvisioningErrors';
import { trackEvent, ProvisioningStep } from '../client/OCAnalytics';
import { useScreenView } from '../hooks/useScreenView';

type AddDeviceStep = 'discover' | 'scanQr' | 'prepare' | 'scanWifi' | 'credentials' | 'join' | 'claim';

const SETUP_MODE_GUIDE_URL = 'https://help.onlycat.com/en/article/i-need-to-reconnect-my-onlycat-to-a-different-wi-fi-network-1ncik1x/';

function rssiToSignalIcon(rssi: number): string {
    if (rssi >= -50) return '/assets/wifi/wifi-signal-strength-5.svg';
    if (rssi >= -60) return '/assets/wifi/wifi-signal-strength-4.svg';
    if (rssi >= -70) return '/assets/wifi/wifi-signal-strength-3.svg';
    if (rssi >= -80) return '/assets/wifi/wifi-signal-strength-2.svg';
    if (rssi >= -90) return '/assets/wifi/wifi-signal-strength-1.svg';
    return '/assets/wifi/wifi-signal-strength-0.svg';
}

// Maps a wizard step onto one of the three progress phases: connect · wi-fi · finish.
function stepPhase(step: AddDeviceStep): number {
    if (step === 'scanWifi' || step === 'credentials' || step === 'join') return 1;
    if (step === 'claim') return 2;
    return 0;
}

// Analytics error codes reuse the normalized provisioning error keys — already
// free of anything network-identifying (SSIDs, passwords).
function provisioningErrorCode(error: unknown): string {
    const message = (error as Error)?.message ?? error;
    return normalizeProvisioningErrorMessage(String(message)).slice(0, 100);
}

function trackProvisioningError(mode: ProvisioningTransport['mode'] | undefined, step: ProvisioningStep, error: unknown) {
    trackEvent({
        name: 'provisioning_error',
        params: { mode: mode ?? 'none', step, code: provisioningErrorCode(error) }
    });
}

const AddDeviceModal = ({ dismiss }: { dismiss: (data?: null, role?: string) => void }) => {
    const queryClient = useQueryClient();
    // Scopes the setup queries to this modal instance; the query cache outlives the
    // modal, and a reopened modal must not resume from the previous session's results.
    const [sessionKey] = useState(() => Date.now().toString(36) + Math.random().toString(36).slice(2));
    const [step, setStep] = useState<AddDeviceStep>('discover');
    const [transport, setTransport] = useState<ProvisioningTransport | null>(null);
    const [selectedNetwork, setSelectedNetwork] = useState<WifiNetwork | null>(null);
    const [ssid, setSsid] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const { t } = useTranslation('modals');
    const { t: tCommon } = useTranslation('common');

    useScreenView('add_device');

    const bleScan = useBleProvisioningScan(step === 'discover');

    const setupCodeQuery = useQuery({
        queryKey: ['setup/scanDeviceCode', sessionKey],
        networkMode: 'always',
        retry: false,
        refetchOnWindowFocus: false,
        enabled: step === 'scanQr',
        staleTime: 0,
        queryFn: async ({ signal }): Promise<string> => {
            if(!isPlatform('capacitor')) {
                throw new Error(t('add_device.qr_scan_mobile_only'));
            }

            if (!(await BarcodeScanner.isSupported()).supported) {
                throw new Error(t('add_device.barcode_not_supported'));
            }

            return new Promise(async resolve => {
                document.querySelector('body')?.classList.add('barcode-scanner-active');

                const listener = await BarcodeScanner.addListener(
                    'barcodesScanned',
                    async (event: BarcodesScannedEvent) => {
                        const regex = /^(?:https:\/\/onlycat\.app\/)?(.{12})$/;

                        for (const barcode of event.barcodes) {
                            const value = barcode.rawValue;
                            const match = value?.match(regex);

                            if (match) {
                                await listener.remove();
                                document.querySelector('body')?.classList.remove('barcode-scanner-active');
                                await BarcodeScanner.stopScan();
                                Haptics.notification({ type: NotificationType.Success });
                                resolve(match[1]);
                                return;
                            }
                        }
                    },
                );

                BarcodeScanner.startScan({
                    formats: [BarcodeFormat.QrCode]
                });

                signal.addEventListener('abort', async () => {
                    await listener.remove();
                    document.querySelector('body')?.classList.remove('barcode-scanner-active');
                    await BarcodeScanner.stopScan();
                }, { once: true });
            });
        },
    });

    useEffect(() => {
        if (step === 'scanQr' && setupCodeQuery.data) {
            trackEvent({ name: 'provisioning_started', params: { mode: 'softap' } });
            setTransport(new SoftApProvisioningTransport(setupCodeQuery.data));
            setStep('prepare');
        }
    }, [setupCodeQuery.data, step]);

    useEffect(() => {
        if (step === 'scanQr' && setupCodeQuery.isError) {
            trackProvisioningError(undefined, 'qr_scan', setupCodeQuery.error);
        }
    }, [setupCodeQuery.isError, setupCodeQuery.error, step]);

    const prepareQuery = useQuery({
        queryKey: ['setup/prepareDeviceProvisioning', sessionKey, transport?.mode, transport?.displayName],
        networkMode: 'always',
        retry: false,
        refetchOnWindowFocus: false,
        enabled: step === 'prepare' && !!transport,
        staleTime: 0,
        queryFn: async ({ signal }) => {
            await transport!.prepare?.(signal);
            return true;
        }
    });

    useEffect(() => {
        if (step === 'prepare' && prepareQuery.isSuccess) {
            trackEvent({ name: 'provisioning_step', params: { mode: transport!.mode, step: 'connected' } });
            Haptics.notification({ type: NotificationType.Success });
            setStep('scanWifi');
        }
    }, [prepareQuery.isSuccess, step]);

    useEffect(() => {
        if (step === 'prepare' && prepareQuery.isError) {
            trackProvisioningError(transport?.mode, 'connect', prepareQuery.error);
            Haptics.notification({ type: NotificationType.Error });
        }
    }, [prepareQuery.isError, step]);

    // The device needs to be put into setup mode by hand; instead of a generic
    // error we guide the user through it and proceed automatically once the
    // transport observes the device entering setup mode.
    const deviceNotInSetupMode = step === 'prepare'
        && prepareQuery.isError && !prepareQuery.isFetching
        && (prepareQuery.error as Error)?.message === 'device_not_in_setup_mode'
        && !!transport?.waitForSetupMode;

    const waitForSetupModeQuery = useQuery({
        queryKey: ['setup/waitForSetupMode', sessionKey, transport?.mode, transport?.displayName],
        networkMode: 'always',
        retry: false,
        refetchOnWindowFocus: false,
        enabled: deviceNotInSetupMode,
        staleTime: 0,
        queryFn: async ({ signal }) => {
            await transport!.waitForSetupMode!(signal);
            return true;
        }
    });

    useEffect(() => {
        if (deviceNotInSetupMode && waitForSetupModeQuery.isSuccess) {
            trackEvent({ name: 'provisioning_step', params: { mode: transport!.mode, step: 'setup_mode_entered' } });
            // waitForSetupMode resolves connected and ready, so prepare needn't rerun.
            Haptics.notification({ type: NotificationType.Success });
            setStep('scanWifi');
        }
    }, [waitForSetupModeQuery.isSuccess, deviceNotInSetupMode]);

    useEffect(() => {
        if (deviceNotInSetupMode && waitForSetupModeQuery.isError) {
            trackProvisioningError(transport?.mode, 'setup_mode_wait', waitForSetupModeQuery.error);
        }
    }, [waitForSetupModeQuery.isError, deviceNotInSetupMode]);

    const scanNetworksQuery = useQuery({
        queryKey: ['setup/scanWifiNetworks', sessionKey, transport?.mode, transport?.displayName],
        networkMode: 'always',
        retry: false,
        enabled: step === 'scanWifi' && !!transport,
        staleTime: 0,
        refetchInterval: transport?.mode === 'softap' ? 5000 : false,
        queryFn: async ({ signal }) => transport!.scanWifiNetworks(signal),
    });

    useEffect(() => {
        if (step === 'scanWifi' && scanNetworksQuery.isError) {
            trackProvisioningError(transport?.mode, 'wifi_scan', scanNetworksQuery.error);
        }
    }, [scanNetworksQuery.isError, step]);

    const joinNetworkMutation = useMutation({
        mutationKey: ['setup/joinWifiNetwork', sessionKey, transport?.mode, transport?.displayName],
        mutationFn: async () => {
            // The channel only applies while the SSID still matches the scanned network.
            const channel = selectedNetwork?.ssid === ssid ? selectedNetwork.channel : undefined;
            await transport!.joinWifiNetwork(ssid, password, { channel });
        },
        onMutate: () => {
            trackEvent({ name: 'provisioning_step', params: { mode: transport!.mode, step: 'wifi_join_started' } });
            setStep('join');
        },
        onSuccess: () => {
            trackEvent({ name: 'provisioning_step', params: { mode: transport!.mode, step: 'wifi_joined' } });
            queryClient.removeQueries({ queryKey: ['setup/scanWifiNetworks'] });
            Haptics.notification({ type: transport?.checkConnectivityOnClaim ? NotificationType.Warning : NotificationType.Success });
            setStep('claim');
        },
        onError: (error) => {
            trackProvisioningError(transport?.mode, 'wifi_join', error);
            Haptics.notification({ type: NotificationType.Error });
        }
    });

    const claimDeviceMutation = useMutation({
        mutationKey: ['setup/claimDevice', sessionKey],
        retry: transport?.checkConnectivityOnClaim ? 3 : false,
        retryDelay: 5000,
        mutationFn: async (): Promise<any> => {
            return OCGatewayClient.request('claimDevice', {
                deviceId: transport!.deviceId,
                checkConnectivity: !!transport?.checkConnectivityOnClaim
            });
        },
        onSuccess: () => {
            trackEvent({ name: 'provisioning_completed', params: { mode: transport!.mode } });
            // The gateway has no realtime device-list push (getDevices' subscribe
            // flag is a server-side TODO), so the list only updates via refetch.
            queryClient.invalidateQueries({ queryKey: ['getDevices'] });
        },
        onError: (error) => {
            trackProvisioningError(transport?.mode, 'claim', error);
        }
    });

    const { mutate: claimDevice, isIdle: claimDeviceIsIdle } = claimDeviceMutation;

    useEffect(() => {
        if (step === 'claim' && transport?.deviceId && claimDeviceIsIdle) {
            claimDevice();
        }
    }, [step, transport?.deviceId, claimDeviceIsIdle, claimDevice]);

    const subtitle = useMemo(() => {
        if (transport?.deviceId) {
            return t('add_device.device_id', { deviceId: transport.deviceId });
        }

        if (transport?.displayName) {
            return transport.displayName;
        }

        return undefined;
    }, [t, transport?.deviceId, transport?.displayName]);

    async function clearWifiNetworkScanCache() {
        await queryClient.cancelQueries({ queryKey: ['setup/scanWifiNetworks'] });
        queryClient.removeQueries({ queryKey: ['setup/scanWifiNetworks'] });
    }

    // Aborts a pending setup-mode wait so its BLE notification listener unwinds.
    async function cancelSetupModeWait() {
        await queryClient.cancelQueries({ queryKey: ['setup/waitForSetupMode'] });
        queryClient.removeQueries({ queryKey: ['setup/waitForSetupMode'] });
    }

    async function selectBleDevice(candidate: ProvisioningCandidate) {
        trackEvent({ name: 'provisioning_started', params: { mode: 'ble' } });
        await bleScan.stop();
        await clearWifiNetworkScanCache();
        await cancelSetupModeWait();
        await transport?.cleanup();
        setSelectedNetwork(null);
        setSsid('');
        setPassword('');
        setTransport(new BleProvisioningTransport(candidate));
        setStep('prepare');
    }

    async function beginSoftApFallback() {
        await bleScan.stop();
        await clearWifiNetworkScanCache();
        await cancelSetupModeWait();
        await transport?.cleanup();
        setTransport(null);
        setSelectedNetwork(null);
        setSsid('');
        setPassword('');
        queryClient.removeQueries({ queryKey: ['setup/scanDeviceCode'] });
        setStep('scanQr');
    }

    async function dismissModal() {
        // Closing anywhere between selecting a device and a successful claim is
        // a funnel drop-off worth measuring.
        if (step !== 'discover' && !claimDeviceMutation.isSuccess) {
            trackEvent({ name: 'provisioning_abandoned', params: { mode: transport?.mode ?? 'none', step } });
        }
        queryClient.cancelQueries({ queryKey: ['setup/scanDeviceCode'] });
        await clearWifiNetworkScanCache();
        await cancelSetupModeWait();
        await transport?.cleanup();
        dismiss();
    }

    function retryClaimDevice() {
        claimDevice();
    }

    function goBackFromCredentials() {
        setStep('scanWifi');
        joinNetworkMutation.reset();
    }

    const deviceName = transport?.displayName || t('add_device.your_device');

    return (
        <IonPage>
            <IonHeader translucent className={step === 'scanQr' ? styles.scanUX : undefined}>
                <IonToolbar>
                    <IonButtons slot="start">
                        {step === 'credentials' && <IonButton onClick={goBackFromCredentials}><IonIcon icon={arrowBack} /></IonButton>}
                    </IonButtons>
                    <IonTitle>
                        <div>{t('add_device.title')}</div>
                        {subtitle && <small>{subtitle}</small>}
                    </IonTitle>
                    <IonButtons slot="primary">
                        <IonButton onClick={dismissModal}>
                            <IonIcon slot="icon-only" icon={close} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
                {step !== 'scanQr' && (
                    <IonToolbar className={styles.progressToolbar}>
                        <SetupStepProgress
                            currentStep={stepPhase(step)}
                            steps={[
                                t('add_device.steps.connect'),
                                t('add_device.steps.wifi'),
                                t('add_device.steps.finish')
                            ]}
                        />
                    </IonToolbar>
                )}
            </IonHeader>
            <IonContent color="light" fullscreen>
                {step === 'discover' && (
                    <div className={styles.wrapper}>
                        <InfoCard
                            className={styles.introCard}
                            icon={bluetooth}
                            title={t('add_device.find_nearby_devices')}
                        >
                            {t('add_device.find_nearby_devices_description')}
                        </InfoCard>

                        <div className={styles.sectionLabel}>
                            <span>{t('add_device.nearby_devices')}</span>
                            {bleScan.isScanning && <IonSpinner name="dots" color="primary" />}
                            {!bleScan.isScanning && (
                                <IonButton fill="clear" size="small" className={styles.sectionAction} onClick={bleScan.retry}>
                                    <IonIcon slot="start" icon={refreshOutline} />
                                    {tCommon('buttons.retry')}
                                </IonButton>
                            )}
                        </div>

                        <IonList className={styles.listCard} lines="full">
                            {bleScan.devices.map(device => (
                                <IonItem button detail key={device.id} onClick={() => selectBleDevice(device)}>
                                    <IonThumbnail slot="start" className={styles.deviceThumbnail}>
                                        <img src="/assets/device/onlycat-device.png" alt={device.name} />
                                    </IonThumbnail>
                                    <IonLabel>
                                        <h2>{device.name}</h2>
                                        {device.deviceId && <p className={styles.deviceId}>{device.deviceId}</p>}
                                    </IonLabel>
                                    {typeof device.rssi === 'number' && <IonIcon slot="end" className={styles.signalIcon} src={rssiToSignalIcon(device.rssi)} />}
                                </IonItem>
                            ))}
                            {!bleScan.devices.length && (
                                <IonItem lines="none">
                                    <IonSpinner slot="start" name="dots" color="primary" />
                                    <IonLabel color="medium">{t('add_device.scanning_for_devices')}</IonLabel>
                                </IonItem>
                            )}
                        </IonList>

                        {bleScan.error && (
                            <IonNote color="danger" className={styles.errorNote}>
                                {translateProvisioningError(bleScan.error, t)}
                            </IonNote>
                        )}

                        <IonButton expand="block" fill="outline" className={styles.scanFallback} onClick={beginSoftApFallback}>
                            <IonIcon slot="start" icon={qrCode} />
                            {t('add_device.cant_see_device')}
                        </IonButton>
                    </div>
                )}

                {step === 'scanQr' && (
                    <div className={styles.scanSlide}>
                        {setupCodeQuery.isFetching && <>
                            <div className={`${styles.scanUX} ${styles.viewfinder}`}>
                                <span className={`${styles.corner} ${styles.cornerTopLeft}`} />
                                <span className={`${styles.corner} ${styles.cornerTopRight}`} />
                                <span className={`${styles.corner} ${styles.cornerBottomLeft}`} />
                                <span className={`${styles.corner} ${styles.cornerBottomRight}`} />
                                <span className={styles.scanLine} />
                            </div>
                            <div className={`${styles.scanUX} ${styles.scanInstructions}`}>
                                <IonIcon icon={qrCode} />
                                <div>
                                    <h3>{t('add_device.scan_setup_code')}</h3>
                                    <p>{t('add_device.scan_instructions')}</p>
                                </div>
                            </div>
                        </>}
                        {setupCodeQuery.isError && !setupCodeQuery.isFetching &&
                            <IonCard>
                                <IonCardHeader>
                                    <IonCardTitle>{t('add_device.error_scanning_code')}</IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent>
                                    {translateProvisioningError(setupCodeQuery.error, t)}
                                </IonCardContent>
                                <IonButton fill="clear" onClick={() => setupCodeQuery.refetch()}>{tCommon('buttons.retry')}</IonButton>
                            </IonCard>
                        }
                    </div>
                )}

                {step === 'prepare' && (
                    <>
                        {prepareQuery.isFetching &&
                            <div className={styles.centerState}>
                                <div className={styles.pulseIcon}>
                                    <span className={styles.pulseIconInner}>
                                        <IonIcon icon={transport?.mode === 'ble' ? bluetooth : qrCode} />
                                    </span>
                                </div>
                                <h2 className={styles.stateTitle}>{t('add_device.connecting')}</h2>
                                <p className={styles.stateText}>{t('add_device.connecting_message', { name: deviceName })}</p>
                                <IonSpinner className={styles.stateSpinner} />
                            </div>
                        }
                        {deviceNotInSetupMode &&
                            <div className={styles.wrapper}>
                                <InfoCard
                                    className={styles.introCard}
                                    icon={settingsOutline}
                                    title={t('add_device.setup_mode_title')}
                                >
                                    {t('add_device.setup_mode_description', { name: deviceName })}
                                </InfoCard>

                                <div className={styles.sectionLabel}>
                                    <span>{t('add_device.setup_mode_steps_label')}</span>
                                </div>
                                <IonList className={styles.listCard} lines="full">
                                    <IonItem>
                                        <span slot="start" className={styles.stepNumber}>1</span>
                                        <IonLabel className="ion-text-wrap">{t('add_device.setup_mode_step_1')}</IonLabel>
                                    </IonItem>
                                    <IonItem>
                                        <span slot="start" className={styles.stepNumber}>2</span>
                                        <IonLabel className="ion-text-wrap">{t('add_device.setup_mode_step_2')}</IonLabel>
                                    </IonItem>
                                    <IonItem lines="none">
                                        <span slot="start" className={styles.stepNumber}>3</span>
                                        <IonLabel className="ion-text-wrap">{t('add_device.setup_mode_step_3')}</IonLabel>
                                    </IonItem>
                                </IonList>

                                {waitForSetupModeQuery.isFetching &&
                                    <div className={styles.waitingRow}>
                                        <IonSpinner name="dots" color="primary" />
                                        <span>{t('add_device.setup_mode_waiting')}</span>
                                    </div>
                                }
                                {waitForSetupModeQuery.isError && !waitForSetupModeQuery.isFetching && <>
                                    <IonNote color="danger" className={styles.errorNote}>
                                        {translateProvisioningError(waitForSetupModeQuery.error, t)}
                                    </IonNote>
                                    <IonButton expand="block" fill="outline" className={styles.primaryButton} onClick={() => waitForSetupModeQuery.refetch()}>
                                        <IonIcon slot="start" icon={refreshOutline} />
                                        {tCommon('buttons.retry')}
                                    </IonButton>
                                </>}

                                <IonButton expand="block" fill="clear" onClick={() => Browser.open({ url: SETUP_MODE_GUIDE_URL })}>
                                    <IonIcon slot="start" icon={openOutline} />
                                    {t('add_device.setup_mode_guide')}
                                </IonButton>
                            </div>
                        }
                        {prepareQuery.isError && !prepareQuery.isFetching && !deviceNotInSetupMode &&
                            <div className={styles.wrapper}>
                                <IonCard>
                                    <IonCardHeader>
                                        <IonCardTitle>{t('add_device.couldnt_connect_to_device')}</IonCardTitle>
                                    </IonCardHeader>
                                    <IonCardContent>
                                        <p>{transport?.mode === 'ble' ? t('add_device.couldnt_connect_ble_message') : t('add_device.couldnt_connect_message')}</p>
                                        <small>{translateProvisioningError(prepareQuery.error, t)}</small>
                                    </IonCardContent>
                                    <IonButton fill="clear" onClick={() => prepareQuery.refetch()}>{tCommon('buttons.retry')}</IonButton>
                                    {transport?.mode === 'ble' && <IonButton fill="clear" onClick={beginSoftApFallback}>{t('add_device.cant_see_device')}</IonButton>}
                                </IonCard>
                            </div>
                        }
                    </>
                )}

                {step === 'scanWifi' && (
                    <div className={styles.wrapper}>
                        <InfoCard
                            className={styles.introCard}
                            icon={wifi}
                            title={t('add_device.wifi_setup')}
                        >
                            {t('add_device.wifi_setup_description')}
                        </InfoCard>

                        <div className={styles.sectionLabel}>
                            <span>{t('add_device.wifi_networks')}</span>
                            {scanNetworksQuery.isFetching && <IonSpinner name="dots" color="primary" />}
                        </div>

                        <IonList className={styles.listCard} lines="full">
                            {scanNetworksQuery.data && scanNetworksQuery.data.map((network: WifiNetwork) => (
                                <IonItem button detail={false} key={network.ssid} onClick={() => { setSelectedNetwork(network); setSsid(network.ssid); setPassword(''); setStep('credentials'); }}>
                                    <IonLabel>{network.ssid}</IonLabel>
                                    {!!network.auth && <IonIcon slot="end" icon={lockClosed} size="small" className={styles.authIcon} />}
                                    {typeof network.rssi === 'number' && <IonIcon slot="end" className={styles.signalIcon} src={rssiToSignalIcon(network.rssi)} />}
                                </IonItem>
                            ))}
                            <IonItem button detail={false} className={styles.accentItem} onClick={() => { setSelectedNetwork(null); setSsid(''); setPassword(''); setStep('credentials'); }}>
                                <IonIcon slot="start" icon={addOutline} color="primary" />
                                <IonLabel>{t('add_device.other_network')}</IonLabel>
                            </IonItem>
                        </IonList>
                        {scanNetworksQuery.error && (
                            <IonNote color="danger" className={styles.errorNote}>
                                {translateProvisioningError(scanNetworksQuery.error, t)}
                            </IonNote>
                        )}
                    </div>
                )}

                {step === 'credentials' && (
                    <div className={styles.wrapper}>
                        <div className={styles.sectionLabel}>
                            <span>{t('add_device.wifi_setup')}</span>
                        </div>
                        <IonList className={styles.listCard} lines="full">
                            <IonItem>
                                <IonInput labelPlacement="stacked" type="text" label={t('add_device.network_name')} placeholder={t('add_device.network_name_placeholder')} autocomplete="off" value={ssid} onIonInput={e => setSsid(e.target.value as string)} />
                            </IonItem>
                            <IonItem lines="none">
                                <IonInput labelPlacement="stacked" type="password" label={t('add_device.password')} autocomplete="off" value={password} onIonInput={e => setPassword(e.target.value as string)}>
                                    <IonInputPasswordToggle slot="end" />
                                </IonInput>
                            </IonItem>
                        </IonList>
                        <div className={styles.securityNote}>
                            <IonIcon icon={shieldCheckmarkOutline} />
                            <span>{t('add_device.password_security_note')}</span>
                        </div>
                        <IonButton expand="block" className={styles.primaryButton} onClick={() => joinNetworkMutation.mutate()} disabled={!ssid}>{t('add_device.join')}</IonButton>
                    </div>
                )}

                {step === 'join' && (
                    <>
                        {joinNetworkMutation.isPending &&
                            <div className={styles.centerState}>
                                <IonSpinner className={styles.stateSpinner} />
                                <h2 className={styles.stateTitle}>{t('add_device.joining_network')}</h2>
                                <p className={styles.stateText}>{t('add_device.joining_network_message', { ssid })}</p>
                            </div>
                        }
                        {joinNetworkMutation.isError && !joinNetworkMutation.isPending &&
                            <div className={styles.wrapper}>
                                <IonCard>
                                    <IonCardHeader>
                                        <IonCardTitle>{t('add_device.couldnt_join_network')}</IonCardTitle>
                                    </IonCardHeader>
                                    <IonCardContent>
                                        {translateProvisioningError(joinNetworkMutation.error, t)}
                                    </IonCardContent>
                                    <IonButton fill="clear" onClick={() => setStep('scanWifi')}>{t('add_device.go_back')}</IonButton>
                                </IonCard>
                            </div>
                        }
                    </>
                )}

                {step === 'claim' && (
                    <>
                        {!transport?.deviceId &&
                            <div className={styles.wrapper}>
                                <IonCard>
                                    <IonCardHeader>
                                        <IonCardTitle>{t('add_device.couldnt_identify_device')}</IonCardTitle>
                                    </IonCardHeader>
                                    <IonCardContent>
                                        {t('add_device.couldnt_identify_device_message')}
                                    </IonCardContent>
                                    <IonButton fill="clear" onClick={beginSoftApFallback}>{t('add_device.scan_setup_code')}</IonButton>
                                </IonCard>
                            </div>
                        }
                        {claimDeviceMutation.isPending &&
                            <div className={styles.centerState}>
                                <IonSpinner className={styles.stateSpinner} />
                                <h2 className={styles.stateTitle}>{t('add_device.adding_device')}</h2>
                                <p className={styles.stateText}>{t('add_device.adding_device_message')}</p>
                            </div>
                        }
                        {claimDeviceMutation.isError &&
                            <div className={styles.wrapper}>
                                <IonCard>
                                    <IonCardHeader>
                                        <IonCardTitle>{t('add_device.couldnt_add_device')}</IonCardTitle>
                                    </IonCardHeader>
                                    <IonCardContent>
                                        <p>{t('add_device.couldnt_add_device_message')}</p>
                                        <small>{translateProvisioningError(claimDeviceMutation.error, t)}</small>
                                    </IonCardContent>
                                    <IonButton fill="clear" onClick={retryClaimDevice}>{tCommon('buttons.retry')}</IonButton>
                                </IonCard>
                            </div>
                        }
                        {claimDeviceMutation.isSuccess &&
                            <div className={styles.centerState}>
                                <div className={styles.successIcon}>
                                    <IonIcon icon={checkmark} />
                                </div>
                                <h2 className={styles.stateTitle}>{t('add_device.device_added')}</h2>
                                <p className={styles.stateText}>{t('add_device.device_added_message')}</p>
                                <IonButton expand="block" className={styles.stateButton} onClick={dismissModal}>{t('add_device.done')}</IonButton>
                            </div>
                        }
                    </>
                )}
            </IonContent>
        </IonPage>
    );
};

function translateProvisioningError(error: any, t: (key: string) => string): string {
    const message = normalizeProvisioningErrorMessage(error?.message ? String(error.message) : String(error));
    const key = `add_device.${message}`;

    if (provisioningErrorTranslationKeys.has(message)) {
        return t(key);
    }

    return message;
}

export default AddDeviceModal;
